import { scrollSmoothlyToBottom } from '@/helpers/scroll'
import { SCROLL_INITIAL_WAIT_TIME, SCROLL_OFFSET } from '@/components/_shared/Form/form-settings'

export const THEMES = {
  default: 'default',
  dark: 'dark',
}

export const VARIANTS = {
  default: 'default',
  red: 'red',
  orange: 'orange',
}

/**
 * Use this to change a field's value from within your component.
 *
 * @param {String} id - Id of the field to be set
 * @param {*} value - New value of the field
 * @param {Array} fields - List of fields that holds the field to be changed
 */
export function setValue(vm, id, value, fields) {
  const fieldIndex = getFieldIndexById(id, fields)
  if (fieldIndex < 0) throw new Error(`Could not set value "${value}" of field with id "${id}".`)
  vm.$set(fields[fieldIndex].props, 'value', value)
}

/**
 * Returns the index of the field for the given id in the given fields array.
 *
 * @param {String} id - Id of the field
 * @param {Array} fields - List of fields that holds the field
 *
 * @returns {Number} Field index in the field array.
 */
export function getFieldIndexById(id, fields) {
  return fields.findIndex(({ props }) => props.id === id)
}

/**
 * Returns a list of field ids.
 *
 * @param {Array} fields - List of fields that holds the field
 *
 * @returns {Array} A list of field ids.
 */
export function getFieldIds(fields) {
  return fields.map(({ props }) => props.id)
}

export function populateSelectField(vm, id, values, fields) {
  const fieldIndex = getFieldIndexById(id, fields)
  vm.$set(fields[fieldIndex].props, 'options', [
    ...fields[fieldIndex].optionsPlaceholder,
    ...values,
  ])
}

export function getFieldById(id, fields) {
  return fields[getFieldIndexById(id, fields)]
}

export async function scrollIntoViewport(currentTarget) {
  scrollSmoothlyToBottom(currentTarget, {
    initialWaitTime: SCROLL_INITIAL_WAIT_TIME,
    includeHeader: false,
    offset: SCROLL_OFFSET,
    hasToBeOutsideViewport: true,
  })
}

/**
 * Returns the first error message for each error by id (extracted from object keys).
 *
 * @param {Object} errorsById Object of errors with multiple messages.
 * @param {Object} options Object of options
 * @param {String} options.prefix Optional prefix to pick erros of specific keys (ids could be duplicate e.g. 'bill_address.zipcode' and 'ship_address.zipcode').
 * @param {Array} options.permittedIds Only return errors for given ids (leave blank to allow all).
 *
 * @returns {Object} First error message for each field
 */
export const getFirstFieldErrorsById = (
  errorsById,
  { prefix = undefined, permittedIds = [] } = {}
) => {
  if (!errorsById) return {}
  return Object.keys(errorsById).reduce((fieldErrors, key) => {
    if (!prefix || key.includes(prefix)) {
      const id = getLastFieldKeySegment(key)
      // Nested associations error keys are not suffixed with `_id` but form fields are, e.g., state -> state_id.
      // Suffixing it with `_id` will allow capturing these errors and relating it to their specific fields.
      // This will only happen if permitted fields was provided!!!
      const associatedObjectId = `${id}_id`

      if (permittedIds.length <= 0 || permittedIds.includes(id))
        fieldErrors[id] = errorsById[key][0]
      else if (permittedIds.includes(associatedObjectId))
        fieldErrors[associatedObjectId] = errorsById[key][0]
    }
    return fieldErrors
  }, {})
}

const getLastFieldKeySegment = key => {
  const keySegments = key.split('.')
  return keySegments[keySegments.length - 1]
}
